export interface TerminalCommand {
  command: string;
  output: string;
}

export interface Chapter {
  id: string;
  number: number;
  title: string;
  subtitle: string;
  content: string;
  terminalCommands?: TerminalCommand[];
}

export const chapters: Chapter[] = [
  {
    id: "prologue",
    number: 0,
    title: "Prologue",
    subtitle: "Why build an OS?",
    content: `
# Why Build an OS?

Most developers never look below the abstractions. You call \`malloc()\`, the memory appears. You call \`printf()\`, text shows up. You fork a process, it runs. Magic.

But it's not magic. Someone wrote that magic. And understanding it changes how you think about every line of code you'll ever write.

This is the story of building three operating systems from scratch — each one going deeper than the last. No tutorials followed blindly. Just source code, panic messages, and a lot of "why the hell is this segfaulting."

## The Stack

\`\`\`
Your App
────────
libc / syscalls
────────
Kernel (← we build this)
────────
Hardware
\`\`\`

Everything above the kernel is a lie the kernel tells you. We're going to write the liar.

## What You'll See Here

- **Joe's OS** — x86, C++, interrupts, and a tiny process table
- **OSx86** — x86 bootloader in assembly, protected mode, C kernel  
- **RISC-V OS** — virtual memory, processes, syscalls, a real shell

Each one broke in new and interesting ways. Every panic message taught something.
    `,
  },
  {
    id: "joeos",
    number: 1,
    title: "Joe's OS",
    subtitle: "x86, C++, interrupts, and a tiny process table",
    content: `
# Joe's OS — From Bare Metal to a Tiny Shell

Joe's OS started as a minimal kernel, then grew into a tiny interactive system with interrupts, a timer, keyboard input, and a demo process table.

The goal here is not to pretend it is a full multitasking OS. The goal is to show the real moving parts clearly, one block at a time.

## Kernel State

The current kernel keeps its state in a few simple globals: VGA cursor position, keyboard buffer indexes, timer ticks, and a fixed-size process table.

\`\`\`cpp
enum class ProcessState : u8 {
    Unused,
    Ready,
    Running,
    Exited,
};

struct Process {
    u32 pid;
    char name[16];
    ProcessState state;
    u32 cpu_ticks;
    u32 start_tick;
};
\`\`\`

This is intentionally small. Each process slot stores just enough information to show state in the shell and to simulate a scheduler.

## Process Table

\`\`\`cpp
Process g_processes[kMaxProcesses];
u32 g_process_count = 0;
u32 g_next_pid = 1;
u32 g_current_process_index = 0;
u32 g_process_slice_ticks = 0;
\`\`\`

These globals are the kernel's bookkeeping layer.

- \`g_processes\` holds the fixed set of process slots.
- \`g_process_count\` tracks how many slots are live.
- \`g_next_pid\` gives every new process a unique id.
- \`g_current_process_index\` points at the currently selected process.
- \`g_process_slice_ticks\` counts timer ticks for round-robin rotation.

## Process Creation

\`\`\`cpp
Process* create_process(const char* name) {
    for (u32 i = 0; i < kMaxProcesses; ++i) {
        if (g_processes[i].state == ProcessState::Unused) {
            Process& process = g_processes[i];
            process.pid = g_next_pid++;
            copy_process_name(process.name, name);
            process.state = ProcessState::Ready;
            process.cpu_ticks = 0;
            process.start_tick = static_cast<u32>(g_timer_ticks);
            ++g_process_count;
            return &process;
        }
    }

    return nullptr;
}
\`\`\`

This function does not create a real OS process yet. It reserves a table slot, copies the name into kernel-owned storage, and marks the entry as ready.

## Round-Robin Scheduling

\`\`\`cpp
void schedule_processes() {
    if (g_process_count == 0) {
        return;
    }

    ++g_process_slice_ticks;
    Process& current = g_processes[g_current_process_index];
    if (current.state != ProcessState::Unused && current.state != ProcessState::Exited) {
        ++current.cpu_ticks;
    }

    if (g_process_slice_ticks < kProcessTimeSliceTicks) {
        return;
    }

    g_process_slice_ticks = 0;

    if (current.state == ProcessState::Running) {
        current.state = ProcessState::Ready;
    }

    for (u32 offset = 1; offset <= kMaxProcesses; ++offset) {
        const u32 candidate_index = static_cast<u32>((g_current_process_index + offset) % kMaxProcesses);
        Process& candidate = g_processes[candidate_index];
        if (candidate.state == ProcessState::Ready) {
            candidate.state = ProcessState::Running;
            g_current_process_index = candidate_index;
            return;
        }
    }

    if (current.state != ProcessState::Unused) {
        current.state = ProcessState::Running;
    }
}
\`\`\`

The timer interrupt calls this function. Every few ticks, the kernel picks the next ready slot and marks it as running.

That gives the shell a believable process model without needing full context switching yet.

## Shell Commands

\`\`\`cpp
void handle_command(const char* command) {
    if (strings_equal(command, "ps")) {
        print_process_table();
        return;
    }

    if (strings_equal(command, "proc")) {
        Process* process = current_process();
        if (process == nullptr) {
            print("No processes are running yet.\\n");
            return;
        }

        print("Current process: ");
        print(process->name);
        print(" (pid ");
        print_u32(process->pid);
        print(", ");
        print_process_state(process->state);
        print(")\\n");
        return;
    }
}
\`\`\`

The shell is just a command dispatcher. It does not parse a full language; it recognizes a few commands and prints useful state.

## Interrupts and Boot

\`\`\`cpp
extern "C" void interrupt_handler(InterruptFrame* frame) {
    if (frame->interrupt_number == 32) {
        ++g_timer_ticks;
        schedule_processes();
        send_eoi(32);
        return;
    }

    if (frame->interrupt_number == 33) {
        const u8 scancode = inb(0x60);
        if ((scancode & 0x80) == 0) {
            if (scancode == 0x1C) {
                enqueue_key('\\n');
            } else if (scancode == 0x0E) {
                enqueue_key('\\b');
            } else {
                const char mapped = scancode_to_char(scancode);
                if (mapped != '\\0') {
                    enqueue_key(mapped);
                }
            }
        }

        send_eoi(33);
        return;
    }
}
\`\`\`

The timer interrupt keeps time and drives the scheduler. The keyboard interrupt feeds the shell through a ring buffer.

\`\`\`cpp
extern "C" void kernel_main() {
    serial_init();
    clear_screen();
    print("Joe's OS\\n");
    print("Starting kernel services...\\n");

    initialize_idt();
    remap_pic();
    initialize_timer(kPitFrequency);
    set_irq_mask(0, false);
    set_irq_mask(1, false);
    initialize_processes();
    print("Interrupts online. Type 'help' to get started.\\n\\n");
    enable_interrupts();
}
\`\`\`

This is the startup path: bring up I/O, install the interrupt table, start the timer, seed the process table, and then drop into the shell loop.

## What This Taught

- Hardware interrupts are the backbone of input and scheduling.
- A process table can be useful before full multitasking exists.
- Keeping kernel-owned copies of data avoids lifetime bugs.
- Freestanding C++ needs explicit ownership and explicit control flow.
    `,
    terminalCommands: [
      {
        command: "hexdump -C kernel.bin | head -4",
        output: `00000000  55 48 89 e5 48 83 ec 10  be 00 80 0b 00 48 89 75  |UH..H....H..H.u|
00000010  f8 c7 45 fc 00 00 00 00  eb 1a 8b 45 fc 48 98 48  |..E.......E.H.H|
00000020  8d 14 45 00 00 00 00 48  8b 45 f8 0f b6 04 02 84  |..E....H.E......|
00000030  c0 74 0d 8b 45 fc 48 98  48 8d 14 45 00 00 00 00  |.t..E.H.H..E....|`,
      },
      {
        command: "objdump -d kernel.elf | grep -A5 'kernelMain'",
        output: `00001000 <kernelMain>:
    1000: 55           push   %rbp
    1001: 48 89 e5     mov    %rsp,%rbp
    1004: be 00 80 0b  mov    $0xb8000,%esi
    1009: 00 48 89     ...`,
      },
    ],
  },
  {
    id: "osx86",
    number: 2,
    title: "OSx86",
    subtitle: "Bootloader, GDT, and Protected Mode",
    content: `
# OSx86 — Writing a Bootloader

Joe's OS used GRUB as a bootloader. OSx86 goes lower: we write the bootloader itself.

## BIOS and the Boot Sector

When you power on an x86 machine, the BIOS loads the first 512 bytes of your disk into memory at address \`0x7C00\` and jumps to it. Those 512 bytes must end with the magic bytes \`0x55 0xAA\` or the BIOS refuses to boot it.

\`\`\`nasm
[bits 16]
[org 0x7C00]

start:
    cli
    xor ax, ax
    mov ds, ax
    mov ss, ax
    mov sp, 0x7C00
\`\`\`

\`cli\` disables interrupts — we're not ready to handle them yet. \`xor ax, ax\` zeros the segment registers. The CPU starts in 16-bit real mode.

## Loading the Kernel

The bootloader uses BIOS interrupt \`0x13\` to read sectors from disk:

\`\`\`nasm
mov ah, 0x02    ; read sectors function
mov al, 5       ; read 5 sectors
mov ch, 0       ; cylinder 0
mov cl, 2       ; start at sector 2
mov dh, 0       ; head 0
mov bx, 0x1000
mov es, bx
xor bx, bx      ; load to 0x10000
int 0x13
\`\`\`

## The GDT and Protected Mode

Real mode only gives you 1MB of memory. To access more, you switch to 32-bit protected mode. This requires setting up the **Global Descriptor Table (GDT)** — a table that defines memory segments.

\`\`\`nasm
gdt_code:
    dw 0xFFFF       ; limit low
    dw 0x0000       ; base low
    db 0x00         ; base mid
    db 10011010b    ; access: present, ring0, code, executable, readable
    db 11001111b    ; flags: 4KB granularity, 32-bit
    db 0x00         ; base high
\`\`\`

Then flip the protection enable bit in \`cr0\`:

\`\`\`nasm
mov eax, cr0
or eax, 1
mov cr0, eax
jmp 0x08:protected_mode   ; far jump flushes the pipeline
\`\`\`

## The Kernel

Once in protected mode, we jump to the C kernel at \`0x10000\`:

\`\`\`c
void kmain() {
    volatile unsigned short *vga = (volatile unsigned short*)0xB8000;
    const char *msg = "Hello from OSx86!";

    for (int i = 0; msg[i] != '\\0'; i++) {
        vga[i] = (unsigned short)(0x0700 | (unsigned char)msg[i]);
    }
    while (1) {}
}
\`\`\`

\`0x07\` = white text on black background. The character goes in the low byte.

## What This Taught

- The BIOS is just firmware that gives you a tiny set of services before getting out of the way.
- Segment registers are a legacy nightmare from the 8086 era.
- A far jump (\`jmp 0x08:addr\`) is needed after enabling protected mode to flush the CPU's instruction pipeline.
- The boot sector is exactly 512 bytes. Not 511. Not 513.
    `,
    terminalCommands: [
      {
        command: "xxd os.img | head -2",
        output: `00000000: fa31 c08e d88e c08e d0bc 007c b40e b041  .1.........|...A
000001f0: 0000 0000 0000 0000 0000 0000 0000 55aa  ..............U.`,
      },
      {
        command: "nasm -f bin boot.asm -o boot.bin && wc -c boot.bin",
        output: `512 boot.bin`,
      },
      {
        command: "objdump -b binary -m i386 -D boot.bin | head -20",
        output: `boot.bin:     file format binary

Disassembly of section .data:

00000000 <.data>:
   0: fa                    cli
   1: 31 c0                 xor    %eax,%eax
   3: 8e d8                 mov    %eax,%ds
   5: 8e c0                 mov    %eax,%es`,
      },
    ],
  },
  {
    id: "riscv-boot",
    number: 3,
    title: "RISC-V OS — Boot",
    subtitle: "OpenSBI, machine mode, and the kernel entry",
    content: `
# RISC-V OS — Booting on QEMU

The third OS targets RISC-V — a clean, modern ISA with no legacy baggage. We run it on QEMU with OpenSBI as the firmware.

## The Boot Chain

\`\`\`
QEMU → OpenSBI (M-mode) → Our Kernel (S-mode)
\`\`\`

OpenSBI runs in Machine mode (the most privileged level). It sets up the hardware, then jumps to our kernel at \`0x80200000\` in Supervisor mode.

## The Boot Function

\`\`\`c
__attribute__((section(".text.boot")))
__attribute__((naked))
void boot(void) {
    __asm__ __volatile__(
        "mv sp, %[stack_top]\\n"
        "j kernel_main\\n"
        :
        : [stack_top] "r" (__stack_top)
    );
}
\`\`\`

\`naked\` means no compiler-generated prologue/epilogue. We set up the stack pointer manually, then jump to \`kernel_main\`. Before this, \`sp\` is undefined — any function call would corrupt memory.

## The Linker Script

\`\`\`ld
ENTRY(boot)

SECTIONS {
    . = 0x80200000;
    __kernel_base = .;

    .text :{ KEEP(*(.text.boot)); *(.text .text.*); }
    .rodata : ALIGN(4) { *(.rodata .rodata.*); }
    .data : ALIGN(4) { *(.data .data.*); }
    .bss : ALIGN(4) {
        __bss = .;
        *(.bss .bss.*);
        __bss_end = .;
    }

    . = ALIGN(4);
    . += 128 * 1024;   /* 128KB kernel stack */
    __stack_top = .;

    . = ALIGN(4096);
    __free_ram = .;
    . += 64 * 1024 * 1024;  /* 64MB free RAM */
    __free_ram_end = .;
}
\`\`\`

The linker script places \`boot\` first (via \`KEEP(*(.text.boot))\`), defines the stack, and carves out 64MB of free RAM for dynamic allocation.

## BSS Zeroing

\`\`\`c
void kernel_main(void) {
    memset(__bss, 0, (size_t)__bss_end - (size_t)__bss);
    // ...
}
\`\`\`

The BSS section (uninitialized globals) isn't zeroed by the ELF loader in a bare-metal environment. We do it manually.
    `,
    terminalCommands: [
      {
        command: "llvm-nm kernel.elf | grep -E 'boot|stack|kernel_base'",
        output: `80200000 T __kernel_base
80200000 T boot
80241f1c B __stack_top`,
      },
      {
        command: "llvm-objdump -d kernel.elf | head -12",
        output: `kernel.elf:	file format elf32-littleriscv

Disassembly of section .text:

80200000 <boot>:
80200000: 80241537    lui   a0, 0x80241
80200004: f1c50513    addi  a0, a0, -0xe4
80200008: 812a        mv    sp, a0
8020000a: 75e0006f    j     0x80200768 <kernel_main>`,
      },
    ],
  },
  {
    id: "riscv-memory",
    number: 4,
    title: "RISC-V OS — Virtual Memory",
    subtitle: "SV32 page tables and address translation",
    content: `
# Virtual Memory — SV32 Paging

Virtual memory lets each process think it owns the whole address space. The MMU translates virtual addresses to physical ones using page tables.

## SV32 Address Translation

RISC-V's SV32 scheme uses two-level page tables. A 32-bit virtual address splits into:

\`\`\`
 31      22 21      12 11        0
┌──────────┬──────────┬────────────┐
│  VPN[1]  │  VPN[0]  │   offset   │
│ 10 bits  │ 10 bits  │  12 bits   │
└──────────┴──────────┴────────────┘
\`\`\`

- **VPN[1]** indexes into the first-level page table (1024 entries)
- **VPN[0]** indexes into the second-level page table (1024 entries)
- **offset** is added to the physical page address

## Page Table Entry

Each entry is 32 bits:

\`\`\`
 31        10 9  8  7  6  5  4  3  2  1  0
┌────────────┬────┬──┬──┬──┬──┬──┬──┬──┬──┐
│  PPN[1:0]  │ RSW│ D│ A│ G│ U│ X│ W│ R│ V│
└────────────┴────┴──┴──┴──┴──┴──┴──┴──┴──┘
\`\`\`

- **V** = Valid
- **R/W/X** = Read/Write/Execute
- **U** = User-accessible
- **PPN** = Physical Page Number

## map_page Implementation

\`\`\`c
void map_page(uint32_t *table1, uint32_t vaddr,
              paddr_t paddr, uint32_t flags) {
    uint32_t vpn1 = (vaddr >> 22) & 0x3ff;

    if ((table1[vpn1] & PAGE_V) == 0) {
        // second-level table doesn't exist yet, allocate it
        uint32_t pt_paddr = alloc_pages(1);
        table1[vpn1] = ((pt_paddr / PAGE_SIZE) << 10) | PAGE_V;
    }

    uint32_t vpn0 = (vaddr >> 12) & 0x3ff;
    uint32_t *table0 = (uint32_t *)((table1[vpn1] >> 10) * PAGE_SIZE);
    table0[vpn0] = ((paddr / PAGE_SIZE) << 10) | flags | PAGE_V;
}
\`\`\`

## Enabling the MMU

Write the page table address and mode to the \`satp\` CSR:

\`\`\`c
// SATP_SV32 = bit 31 set, enables SV32 mode
// lower bits = physical page number of root page table
csrw satp, (SATP_SV32 | (page_table_paddr / PAGE_SIZE))
sfence.vma   // flush TLB
\`\`\`

After this instruction, every memory access goes through the MMU.

## What This Taught

- Page tables are just arrays in memory. The MMU is a hardware walker.
- Forgetting \`sfence.vma\` after changing \`satp\` = stale TLB entries = mysterious faults.
- The kernel must map itself into every process's page table, or it can't handle traps.
- \`PAGE_U\` flag matters: without it, user-mode code can't access the page even if it's mapped.
    `,
    terminalCommands: [
      {
        command: "python3 -c \"\nvaddr = 0x1000000\nprint(f'VPN[1]: {(vaddr >> 22) & 0x3ff}')\nprint(f'VPN[0]: {(vaddr >> 12) & 0x3ff}')\nprint(f'offset: {vaddr & 0xfff}')\n\"",
        output: `VPN[1]: 4
VPN[0]: 0
offset: 0`,
      },
      {
        command: "llvm-nm kernel.elf | grep free_ram",
        output: `80241000 B __free_ram
84241000 B __free_ram_end`,
      },
    ],
  },
  {
    id: "riscv-processes",
    number: 5,
    title: "RISC-V OS — Processes",
    subtitle: "Context switching, the scheduler, and trap handling",
    content: `
# Processes — Context Switching

A process is just a saved CPU state plus some memory. Switching between processes means saving one CPU state and restoring another.

## The Process Struct

\`\`\`c
struct process {
    int pid;
    int state;
    vaddr_t sp;           // saved stack pointer
    uint32_t *page_table; // pointer to 1st-level page table
    uint8_t stack[8192];  // kernel stack
};
\`\`\`

Each process has its own kernel stack. When a process is not running, its registers are saved on that stack.

## switch_context

\`\`\`c
__attribute__((naked))
void switch_context(uint32_t *prev_sp, uint32_t *next_sp) {
    __asm__ __volatile__(
        "addi sp, sp, -13 * 4\\n"
        "sw ra,  0 * 4(sp)\\n"   // return address
        "sw s0,  1 * 4(sp)\\n"   // callee-saved registers
        // ... s1-s11
        "sw sp, (a0)\\n"          // *prev_sp = sp
        "lw sp, (a1)\\n"          // sp = *next_sp
        // restore s0-s11, ra
        "ret\\n"
    );
}
\`\`\`

Only callee-saved registers need saving — the C calling convention guarantees the caller already saved the rest.

## The Trap Handler

When a syscall or exception fires, the CPU jumps to \`stvec\`. Our handler saves all registers, calls C code, then restores:

\`\`\`c
__attribute__((naked)) void kernel_entry(void) {
    __asm__ __volatile__(
        "csrrw sp, sscratch, sp\\n"  // swap: sp=kernel stack, sscratch=user sp
        "addi sp, sp, -4 * 31\\n"
        // save all 31 registers...
        "csrr a0, sscratch\\n"       // get user sp
        "sw a0, 4 * 30(sp)\\n"       // save it in trap frame
        "addi a0, sp, 4 * 31\\n"
        "csrw sscratch, a0\\n"       // restore sscratch = kernel stack top
        "mv a0, sp\\n"
        "call handle_trap\\n"
        // restore all registers...
        "lw sp, 4 * 30(sp)\\n"       // restore user sp
        "sret\\n"
    );
}
\`\`\`

The \`sscratch\` CSR holds the kernel stack top while in user mode. \`csrrw\` atomically swaps \`sp\` and \`sscratch\`.

## The Scheduler

\`\`\`c
void yield(void) {
    struct process *next = idle_proc;
    for (int i = 0; i < PROCS_MAX; i++) {
        struct process *proc = &procs[(current_proc->pid + i) % PROCS_MAX];
        if (proc->state == PROC_RUNNABLE && proc->pid > 0) {
            next = proc;
            break;
        }
    }
    if (next == current_proc) return;

    // switch page table and kernel stack pointer
    __asm__ __volatile__(
        "sfence.vma\\n"
        "csrw satp, %[satp]\\n"
        "sfence.vma\\n"
        "csrw sscratch, %[sscratch]\\n"
        :
        : [satp] "r" (SATP_SV32 | (next->page_table / PAGE_SIZE)),
          [sscratch] "r" (&next->stack[8192])
        : "memory"
    );

    struct process *prev = current_proc;
    current_proc = next;
    switch_context(&prev->sp, &next->sp);
}
\`\`\`
    `,
    terminalCommands: [
      {
        command: "llvm-nm kernel.elf | grep procs",
        output: `80210e8c B procs`,
      },
      {
        command: "python3 -c \"\nprocs = 0x80210e8c\nproc_size = 4+4+4+4+8192  # pid+state+sp+page_table+stack\nfor i in range(3):\n    stack_top = procs + proc_size*i + 16 + 8192\n    print(f'proc[{i}] kernel stack top: {hex(stack_top)}')\n\"",
        output: `proc[0] kernel stack top: 0x80212e9c
proc[1] kernel stack top: 0x80214eac
proc[2] kernel stack top: 0x80216ebc`,
      },
    ],
  },
  {
    id: "riscv-shell",
    number: 6,
    title: "RISC-V OS — The Shell",
    subtitle: "Syscalls, user space, and a working shell",
    content: `
# The Shell — User Space

The shell runs in user mode. It can't touch kernel memory directly — it has to ask the kernel via syscalls.

## The Syscall Interface

User side:

\`\`\`c
int syscall(int sysno, int arg0, int arg1, int arg2) {
    register int a0 __asm__("a0") = arg0;
    register int a1 __asm__("a1") = arg1;
    register int a2 __asm__("a2") = arg2;
    register int a3 __asm__("a3") = sysno;

    __asm__ __volatile__("ecall"
        : "=r"(a0)
        : "r"(a0), "r"(a1), "r"(a2), "r"(a3)
        : "memory");
    return a0;
}

void putchar(char ch) { syscall(SYS_PUTCHAR, ch, 0, 0); }
int  getchar(void)    { return syscall(SYS_GETCHAR, 0, 0, 0); }
\`\`\`

Kernel side:

\`\`\`c
void handle_syscall(struct trap_frame *f) {
    switch (f->a3) {
        case SYS_PUTCHAR:
            putchar(f->a0);
            break;
        case SYS_GETCHAR:
            while (1) {
                struct sbiret ret = sbi_call(0,0,0,0,0,0, 0, 2);
                if (ret.error >= 0) { f->a0 = ret.error; break; }
                yield();
            }
            break;
    }
}
\`\`\`

## user_entry — The Gate to User Mode

\`\`\`c
__attribute__((naked)) void user_entry(void) {
    __asm__ __volatile__(
        "csrw sepc, %[sepc]       \\n"  // set return address = USER_BASE
        "csrw sstatus, %[sstatus] \\n"  // enable interrupts in user mode
        "sret                     \\n"  // return to user mode
        :
        : [sepc]    "r" (USER_BASE),
          [sstatus] "r" (SSTATUS_SPIE)
    );
}
\`\`\`

\`sret\` drops privilege from S-mode to U-mode and jumps to \`sepc\`. The shell binary is mapped at \`USER_BASE = 0x1000000\`.

## The Shell

\`\`\`c
void main(void) {
    char buf[128];
    while (1) {
        printf("> ");
        int i = 0;
        while (1) {
            char c = getchar();
            putchar(c);
            if (c == '\\r') { putchar('\\n'); break; }
            if (i < 127) buf[i++] = c;
        }
        buf[i] = '\\0';

        if (strcmp(buf, "hello") == 0)
            printf("Hello world from shell!\\n");
        else
            printf("unknown command: %s\\n", buf);
    }
}
\`\`\`

## The Bugs That Happened

Every single one of these was a real panic message:

| scause | meaning | cause |
|--------|---------|-------|
| \`0xc\` | instruction page fault | shell binary not mapped at USER_BASE |
| \`0xf\` | store page fault | stack pages not mapped |
| \`0xf\` | store page fault | \`sscratch\` held wrong address |
| \`0x8\` | ecall | working syscall ✓ |
    `,
    terminalCommands: [
      {
        command: "llvm-objdump -d shell.elf | head -15",
        output: `shell.elf:	file format elf32-littleriscv

Disassembly of section .text:

01000000 <start>:
 1000000: 010105b7    lui   a1, 0x1010
 1000004: 34058593    addi  a1, a1, 0x340
 1000008: 812e        mv    sp, a1
 100000a: 2049        jal   0x1000050 <main>
 100000c: 2801        jal   0x1000010 <exit>`,
      },
      {
        command: "./run.sh",
        output: `[OpenSBI boots...]
> hello
Hello world from shell!
> `,
      },
    ],
  },
  {
    id: "epilogue",
    number: 7,
    title: "What's Next",
    subtitle: "File systems, more syscalls, and going deeper",
    content: `
# What's Next

Three operating systems. Each one taught something the previous one couldn't.

## The Progression

\`\`\`
Joe's OS    → "I can write to hardware memory"
OSx86       → "I understand how the CPU boots"
RISC-V OS   → "I understand how processes, memory, and syscalls work"
\`\`\`

## What the RISC-V OS Still Needs

- **More syscalls** — \`exit\`, \`open\`, \`read\`, \`write\`
- **A filesystem** — right now there's no persistent storage
- **ELF loader** — load programs from disk instead of embedding them
- **Multiple user processes** — the scheduler is ready, just need more processes
- **A proper libc** — \`malloc\`, \`free\`, string functions

## The Real Lesson

The kernel is not magic. It's a program that runs first and lies to every other program about how much memory they have, how many CPUs there are, and whether files exist.

Understanding that lie makes you a better programmer at every level of the stack.

\`\`\`c
// this is all an OS really is at its core:
while (1) {
    wait_for_something_to_happen();
    handle_it();
    return_to_user();
}
\`\`\`

The complexity is in the details. And the details are worth knowing.
    `,
  },
];
